import React, { useEffect, useReducer, useRef } from 'react';
import u2000FormState, {
  U2000FormStateEnumEditMode,
  U2000FormStateEnumState,
  type U2000FormState,
} from '../../../reducer/formReducer/U2000FormState';
import { u2000IntFormState, useU2000FormStateHandlers } from '../../../reducer/formReducer/U2000FormStateHandlers';
import type { CalendarPosition } from '../calendar/U2000CalendarPopup';
import U2000InputCalendarCustom from './U2000InputCalendarCustom';

type U2000InputDateFormData = { date: string };

const FIELD_NAME = 'date';

export interface U2000InputDateProps {
  /** Date ISO `yyyy-MM-dd`. */
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  dateFormat?: string;
  placeHolder?: string;
  className?: string;
  popupPosition?: CalendarPosition;
  popupZIndex?: number;
}

/**
 * Champ date contrôlé (hors formulaire) — même rendu que `U2000InputCalendarCustom` (U3121, formulaires).
 */
const U2000InputDate: React.FC<U2000InputDateProps> = ({
  value,
  onChange,
  disabled = false,
  dateFormat = 'yyyy-MM-dd',
  placeHolder,
  className,
  popupPosition = 'auto',
  popupZIndex = 9999,
}) => {
  const isoValue = value.slice(0, 10);
  const syncingFromParentRef = useRef(false);
  const lastEmittedRef = useRef(isoValue);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const [formState, formStateDispatch] = useReducer(
    u2000FormState,
    isoValue,
    (initialDate): U2000FormState<U2000InputDateFormData> => ({
      ...u2000IntFormState,
      state: U2000FormStateEnumState.EDIT,
      editMode: U2000FormStateEnumEditMode.EDIT,
      current: { date: initialDate },
      old: { date: initialDate },
    }),
  );

  const formStateHandlers = useU2000FormStateHandlers<U2000InputDateFormData>({
    formState,
    formStateDispatch,
  });

  const formDate = formState.current?.date;

  // Valeur externe → état interne (flèches jour préc./suiv., « Aujourd'hui »)
  useEffect(() => {
    const current = (formDate ?? '').slice(0, 10);
    if (current === isoValue) return;
    syncingFromParentRef.current = true;
    formStateHandlers.setData({ date: isoValue });
    lastEmittedRef.current = isoValue;
    // Remettre le flag après le commit React (évite que l’effet child→parent voie syncing=false trop tôt)
    queueMicrotask(() => {
      syncingFromParentRef.current = false;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync parent → form sur isoValue uniquement
  }, [isoValue]);

  // Saisie / calendrier → parent (onChange via ref : identité parent instable ≠ re-emit)
  useEffect(() => {
    if (syncingFromParentRef.current) return;
    const next = typeof formDate === 'string' ? formDate.slice(0, 10) : '';
    if (!next || next.length < 10 || next === lastEmittedRef.current) return;
    lastEmittedRef.current = next;
    onChangeRef.current(next);
  }, [formDate]);

  return (
    <div className={className ?? 'w-44 min-w-44 shrink-0'}>
      <U2000InputCalendarCustom
        formStateHandlers={formStateHandlers}
        fieldName={FIELD_NAME}
        label=""
        readonly={disabled}
        dateFormat={dateFormat}
        placeholder={placeHolder ?? dateFormat}
        popupPosition={popupPosition}
        popupZIndex={popupZIndex}
        popoverInPortal
      />
    </div>
  );
};

export default U2000InputDate;
